import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { UploadButton } from 'src/components/Buttons';
import {
  bulkEditInProgress,
  BulkEditStatus,
} from 'src/components/common/BulkActions/BulkEditStatus';
import { BulkUploadBarcodesBody } from 'src/components/UploadArtifacts/UploadBarcodes';
import {
  barcodeArrayToDict,
  BulkBarcodeForm,
  checkBulkBarcodeErrors,
  checkDuplicateBarcodes,
} from 'src/components/UploadArtifacts/UploadBarcodes/UploadBarcodes.utils';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { CancellableFormFooter } from 'src/modals/common';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { ModalBody, ModalFooter } from 'src/modals/Modal';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditStep,
  Event,
  TicketBarcode,
} from 'src/WebApiController';

import { modalDetails } from '../common/Modals.css';
import { Summary } from '../common/Summary';
import {
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
} from '../Modal/Modal.styled';

export const BulkUploadBarcodes = ({
  updateKey,
  event,
  isLoading,
  isSubmitting,
  onSubmit,
  onClose,
  entityType,
}: {
  updateKey: string;
  event?: Event;
  isLoading?: boolean;
  isSubmitting?: boolean;
  onSubmit: (allBarcodes: { [key: string]: TicketBarcode[] }) => Promise<void>;
  onClose?: () => void;
  entityType: ActionOutboxEntityType.Listing | ActionOutboxEntityType.Sale;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const [alreadyReportedUsedBarcodes, setAlreadyReportedUsedBarcodes] =
    useState<string[]>([]);
  const barcodeAlreadyUsedWarning = useContent(ContentId.BarcodeAlreadyInUsed);

  const { showErrorDialog } = useClientLogContext();

  const { bulkEditHub, progress, initJob } = useBulkEditHub(
    entityType,
    BulkActionType.UpdateBarcodes,
    updateKey
  );
  useEffect(() => {
    initJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const {
    handleSubmit,
    setError,
    formState: { defaultValues },
    setValue,
  } = useFormContext<BulkBarcodeForm>();

  const hasItemsToUpdate =
    defaultValues?.barcodes != null && defaultValues.barcodes.length > 0;

  const [isValidating, setIsValidating] = useState(false);

  const duplicateMsg = useContent(ContentId.DuplicateBarcode);
  const requiredMsg = useContent(ContentId.Required);

  const submitHandler = useCallback(
    async (values: BulkBarcodeForm) => {
      let valuesTrimmed = values.barcodes.map((barcode, index) => ({
        ...barcode,
        barcode:
          (barcode.barcode.trim() ||
            defaultValues?.barcodes?.[index]?.barcode) ??
          '', // Don't allow removing existing barcodes
      }));

      setValue('barcodes', valuesTrimmed);

      let hasErrors = checkBulkBarcodeErrors(
        values.isBulkUpload,
        valuesTrimmed,
        setError,
        duplicateMsg,
        requiredMsg,
        entityType === ActionOutboxEntityType.Listing // allowPartialWithinEntity
      );

      valuesTrimmed = valuesTrimmed.filter((b) => b.barcode);

      if (hasErrors) {
        return;
      }

      hasErrors = Boolean(
        await checkDuplicateBarcodes(
          undefined,
          valuesTrimmed,
          activeAccountWebClientConfig,
          alreadyReportedUsedBarcodes,
          setAlreadyReportedUsedBarcodes,
          (index: number) =>
            setError(`barcodes.${index}.barcode`, {
              type: 'warning',
              message: barcodeAlreadyUsedWarning,
            }),
          () => setIsValidating(true),
          () => setIsValidating(false),
          showErrorDialog,
          event!.virtId
        )
      );

      if (hasErrors) {
        return;
      }

      const valuesDict = barcodeArrayToDict(valuesTrimmed);
      onSubmit(valuesDict);
    },
    [
      activeAccountWebClientConfig,
      alreadyReportedUsedBarcodes,
      barcodeAlreadyUsedWarning,
      defaultValues?.barcodes,
      duplicateMsg,
      entityType,
      event,
      onSubmit,
      requiredMsg,
      setError,
      setValue,
      showErrorDialog,
    ]
  );

  const inProgress = bulkEditInProgress(
    hasBackgroundBulkEditFeature,
    isSubmitting,
    progress
  );

  return (
    <>
      <CancellableFormHeader onClose={onClose}>
        <ConnectedEventEntityHeader
          title={<Content id={ContentId.SaveBarcodes} />}
        />
      </CancellableFormHeader>
      <ModalBody>
        {event && (
          <ModalBodyHeaderContainer>
            <Summary event={event} />
          </ModalBodyHeaderContainer>
        )}
        <ModalBodyDataContainer>
          {isLoading ? (
            <PosSpinner />
          ) : hasItemsToUpdate ? (
            <BulkEditStatus
              entityType={entityType}
              updateKey={updateKey}
              height={'full'}
            >
              <div className={modalDetails}>
                <BulkUploadBarcodesBody disabled={inProgress || isValidating} />
              </div>
            </BulkEditStatus>
          ) : (
            <WarningMessage
              justifyContent="center"
              width="full"
              message={<Content id={ContentId.NoItemsWereEligibleForUpdate} />}
            />
          )}
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        {progress?.step === BulkEditStep.Done ? (
          <Button onClick={onClose} disabled={isSubmitting}>
            <Content id={ContentId.Done} />
          </Button>
        ) : (
          <CancellableFormFooter
            showDialogOnCancel={!inProgress && hasItemsToUpdate}
          >
            <UploadButton
              disabled={
                inProgress || isValidating || isLoading || !hasItemsToUpdate
              }
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSubmit(submitHandler, console.log)();
              }}
              textContentId={ContentId.SaveBarcodes}
            />
          </CancellableFormFooter>
        )}
      </ModalFooter>
    </>
  );
};
